import React from 'react';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';

const CashOnCashYieldTrap = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans animate-slide-in">
      <Navigation />

      {/* Article Header */}
      <section className="bg-white py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm text-gray-500 mb-3">
            Real Estate Strategy · PulseShift Research
          </p>

          <h1 className="text-4xl font-semibold text-[#0A2540] mb-4">
            16.4% Return, Still a Bad Deal
          </h1>

          <p className="text-xl text-[#2E2E2E] max-w-3xl">
            Why income-focused metrics like Cash-on-Cash can mislead investors
            by masking equity erosion and negative total returns.
          </p>
        </div>
      </section>

      {/* Article + PDF */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-12">

          {/* LEFT — TEXT */}
          <div className="lg:w-[58%] space-y-10 text-[#2E2E2E] leading-relaxed">

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              1. Introduction: The Yield Trap
            </h2>

            <p>
              A 16.4% yield is frequently misconstrued as a proxy for total
              performance. In institutional real estate, high distributions
              often mask structural failures in underwriting.
            </p>

            <p>
              Singular reliance on income-focused metrics creates a
              <strong> yield trap</strong> — a scenario where investors maintain
              double-digit payouts while the underlying equity base evaporates.
            </p>

            <p>
              Performance is not defined by the coupon, but by the preservation
              and growth of equity.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              2. The Comfort of the Known: Defining Cash-on-Cash
            </h2>

            <p>
              Cash-on-Cash (CoC) is a levered metric focused exclusively on
              periodic income. It measures immediate liquidity rather than total
              value creation.
            </p>

            <div className="font-mono bg-gray-50 p-4 rounded border">
              Annual Cash Flow ÷ Equity Invested = Cash-on-Cash Return
            </div>

            <blockquote className="border-l-4 border-[#4A6FA5] pl-4 italic text-gray-600">
              “Cash-on-Cash optimizes comfort, not outcomes.”
            </blockquote>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              3. The Blind Spot: What Income-Focused Metrics Ignore
            </h2>

            <p>
              Because CoC is a point-in-time calculation, it is indifferent to
              the health of the principal investment.
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Exit Value:</strong> Realized price at disposition</li>
              <li><strong>Capital Loss:</strong> Impairment of original equity</li>
              <li><strong>Market Repricing:</strong> Cap rate expansion</li>
              <li><strong>Equity Erosion:</strong> Gradual value decay</li>
            </ul>

            <p className="font-semibold">
              In institutional underwriting, the distinction is absolute:
              Income ≠ Performance.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              4. Case Study: High Income, Negative Outcome
            </h2>

            <p>
              A six-year hold can appear operationally healthy while failing as
              an investment.
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>Annual Cash Flow: $180,000</li>
              <li>Equity Invested: $1,100,000</li>
              <li>Cash-on-Cash Return: 16.4%</li>
              <li>Net Equity Loss at Exit: –$60,000</li>
              <li>Levered IRR: –1.9%</li>
            </ul>

            <blockquote className="border-l-4 border-[#4A6FA5] pl-4 italic text-gray-600">
              “High income. Negative outcome.”
            </blockquote>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              5. Strategic Application: When the Metric Works — and When It Breaks
            </h2>

            <p>
              Cash-on-Cash is useful only when liquidity is the objective.
              In short-hold, leverage-heavy, or value-add strategies, it provides
              a false sense of security.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              6. Conclusion: Choosing the Right Question
            </h2>

            <p>
              Cash-on-Cash measures cash flow comfort, not investment success.
              Great underwriting means selecting the metric that answers the
              correct question.
            </p>

            <p className="text-sm text-gray-500 pt-6">
              Source: PulseShift Research | Real Estate Strategy
            </p>
          </div>

          {/* RIGHT — PDF (CLEAN VIEW) */}
          <div className="lg:w-[42%] sticky top-24 self-start">
            <div className="h-[85vh] border rounded-lg overflow-hidden shadow-sm">
              <iframe
                src="/The_Income_Illusion.pdf#view=FitH&navpanes=0&scrollbar=0"
                title="The Income Illusion PDF"
                className="w-full h-full"
              />
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CashOnCashYieldTrap;
